import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { Alert, Box, Stack, Typography } from '@mui/material';

import {
  overallMessage,
  overallSeverity,
  type ReadinessItemStatus,
  type ReadinessResult,
} from '../../lib/readiness';

// ── Status icon ──────────────────────────────────────────────────────────────

function ReadinessIcon({ status }: { status: ReadinessItemStatus }) {
  switch (status) {
    case 'ok':
      return (
        <CheckCircleRoundedIcon sx={{ color: 'success.main', fontSize: 20, flexShrink: 0, mt: 0.1 }} />
      );
    case 'warn':
      return (
        <WarningAmberRoundedIcon
          sx={{ color: 'warning.main', fontSize: 20, flexShrink: 0, mt: 0.1 }}
        />
      );
    case 'blocked':
      return (
        <CancelRoundedIcon sx={{ color: 'error.main', fontSize: 20, flexShrink: 0, mt: 0.1 }} />
      );
    default:
      return (
        <RadioButtonUncheckedRoundedIcon
          sx={{ color: 'action.disabled', fontSize: 20, flexShrink: 0, mt: 0.1 }}
        />
      );
  }
}

// ── Component ────────────────────────────────────────────────────────────────

interface ReadinessChecklistProps {
  readiness: ReadinessResult;
  /**
   * Whether to show the overall verdict `Alert` below the checklist.
   * Default: `true`.
   */
  showVerdict?: boolean;
}

/**
 * Readiness checklist for publication-and-sale readiness.
 *
 * Renders four gate rows (Product card · Availability · Publication · Price)
 * with colour-coded icons and human-readable detail, followed by an overall
 * verdict Alert.
 *
 * Used in:
 *  - `ProductDetailDrawer` — list-view quick-check panel
 *  - `ProductEditPage` hero card — live-updating as the merchant edits the form
 *
 * The `readiness` value is computed by `computeReadiness()` from
 * `catalog/lib/readiness.ts`.
 */
export function ReadinessChecklist({ readiness, showVerdict = true }: ReadinessChecklistProps) {
  return (
    <Stack spacing={1.25}>
      {readiness.items.map((item) => (
        <Stack key={item.label} direction="row" spacing={1.25} alignItems="flex-start">
          <ReadinessIcon status={item.status} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
              {item.label}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ lineHeight: 1.4, display: 'block' }}
            >
              {item.detail}
            </Typography>
          </Box>
        </Stack>
      ))}

      {showVerdict && (
        <Alert
          severity={overallSeverity(readiness.overall)}
          variant="outlined"
          sx={{ py: 0.75, mt: 0.25 }}
        >
          <Typography variant="body2" fontWeight={500}>
            {overallMessage(readiness.overall)}
          </Typography>
        </Alert>
      )}
    </Stack>
  );
}
